import React, { useEffect, useRef } from 'react';
import { RemoteParticipant, RemoteTrack } from 'twilio-video';

interface RemoteParticipantViewProps {
  participant: RemoteParticipant;
  isMain?: boolean; // Whether this is the main video display
}

export const RemoteParticipantView: React.FC<RemoteParticipantViewProps> = ({ participant, isMain = false }) => {
  const videoContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = videoContainerRef.current;
    if (!container) return;

    // Track which tracks we've already attached to avoid duplicates
    const attachedTracks = new Set<RemoteTrack>();

    const attachTrack = (track: RemoteTrack) => {
      // Skip if already attached
      if (attachedTracks.has(track)) {
        return;
      }

      if (track.kind === 'video') {
        // Remove any existing video elements first (in case of re-attachment)
        const existingVideos = container.querySelectorAll('video');
        existingVideos.forEach(video => {
          if (video.parentNode === container) {
            container.removeChild(video);
          }
        });

        const videoElement = track.attach();
        // Style video element to fill container
        if (videoElement instanceof HTMLVideoElement) {
          videoElement.style.width = '100%';
          videoElement.style.height = '100%';
          videoElement.style.objectFit = 'cover';
        }
        container.appendChild(videoElement);
        attachedTracks.add(track);
      } else if (track.kind === 'audio') {
        // Audio tracks are handled automatically by Twilio
        track.attach();
        attachedTracks.add(track);
      }
    };

    const detachTrack = (track: RemoteTrack) => {
      if (track.kind === 'video') {
        const elements = track.detach();
        elements.forEach((element) => {
          if (element.parentNode === container) {
            container.removeChild(element);
          }
        });
        attachedTracks.delete(track);
      } else if (track.kind === 'audio') {
        track.detach();
        attachedTracks.delete(track);
      }
    };

    // Attach existing tracks (only video tracks - prioritize camera over screen share)
    const videoTracks: RemoteTrack[] = [];
    participant.tracks.forEach((publication) => {
      if (publication.track && publication.isSubscribed && publication.track.kind === 'video') {
        videoTracks.push(publication.track);
      }
    });

    // Attach the first video track (typically camera, not screen share)
    if (videoTracks.length > 0) {
      attachTrack(videoTracks[0]);
    }

    // Listen for new tracks
    participant.on('trackSubscribed', attachTrack);
    participant.on('trackUnsubscribed', detachTrack);

    return () => {
      participant.off('trackSubscribed', attachTrack);
      participant.off('trackUnsubscribed', detachTrack);
      
      // Clean up all attached elements
      Array.from(container.querySelectorAll('video, audio')).forEach((element) => {
        if (element.parentNode === container) {
          container.removeChild(element);
        }
      });
      attachedTracks.clear();
    };
  }, [participant]);

  return (
    <div 
      ref={videoContainerRef} 
      className={`w-full h-full flex items-center justify-center relative ${
        isMain ? '' : 'rounded-lg overflow-hidden'
      }`}
    >
      {/* Video elements will be attached here by Twilio */}
      {(!videoContainerRef.current || videoContainerRef.current.children.length === 0) && (
        <div className="absolute inset-0 flex items-center justify-center bg-base-200">
          <div className="text-base-content/50 text-center">
            <div className={`${isMain ? 'w-24 h-24' : 'w-12 h-12'} bg-base-300 rounded-full flex items-center justify-center mx-auto mb-2`}>
              <span className={`${isMain ? 'text-3xl' : 'text-lg'} font-semibold`}>
                {participant.identity && participant.identity.length > 0 
                  ? participant.identity[0].toUpperCase() 
                  : 'P'}
              </span>
            </div>
            <p className={isMain ? 'text-lg font-medium' : 'text-sm'}>
              {participant.identity || 'Participant'}
            </p>
          </div>
        </div>
      )}
      
      {/* Participant name overlay (only on main video) */}
      {isMain && videoContainerRef.current && videoContainerRef.current.children.length > 0 && (
        <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-sm font-medium">
          {participant.identity || 'Participant'}
        </div>
      )}
    </div>
  );
};

